import React, { useEffect, useRef, useState } from 'react';
import { isSameWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { WeekPickerCalendar } from './payroll/WeekPickerCalendar';
import { toLocalDateString } from '../utils/payrollPeriodLocal';
import { formatCompanyDateRange } from '../utils/companyDateFormat';

export type WeekNavigatorVariant = 'default' | 'subtle' | 'neutral';

export interface WeekNavigatorProps {
  weekStart: Date;
  weekEnd: Date;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  currentWeek: Date;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onCurrentWeek: () => void;
  onWeekSelect: (weekStartYmd: string) => void;
  disabled?: boolean;
  currentWeekLabel?: string;
  className?: string;
  variant?: WeekNavigatorVariant;
  endContent?: React.ReactNode;
  showCurrentWeekButton?: boolean;
  dateFormat?: 'short' | 'long';
}

const variantStyles: Record<
  WeekNavigatorVariant,
  {
    navBtn: string;
    pickerBtn: string;
    currentBtn: string;
    dateText: string;
  }
> = {
  default: {
    navBtn: 'p-2 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed',
    currentBtn: 'px-4 py-2 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-lg font-medium text-gray-900',
  },
  subtle: {
    navBtn:
      'p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed',
    currentBtn:
      'px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-sm font-semibold text-gray-900',
  },
  neutral: {
    navBtn: 'p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg bg-white hover:bg-neutral-50 text-left min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed',
    currentBtn:
      'px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-sm font-medium text-neutral-800',
  },
};

export function WeekNavigator({
  weekStart,
  weekEnd,
  weekStartsOn,
  currentWeek,
  onPreviousWeek,
  onNextWeek,
  onCurrentWeek,
  onWeekSelect,
  disabled = false,
  currentWeekLabel = 'Current week',
  className = '',
  variant = 'default',
  endContent,
  showCurrentWeekButton = true,
}: WeekNavigatorProps) {
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const styles = variantStyles[variant];

  const weekRangeLabel = formatCompanyDateRange(weekStart, weekEnd);

  const isCurrentWeek = isSameWeek(currentWeek, new Date(), { weekStartsOn });

  useEffect(() => {
    if (!weekPickerOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setWeekPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [weekPickerOpen]);

  const handlePrevious = () => {
    setWeekPickerOpen(false);
    onPreviousWeek();
  };

  const handleNext = () => {
    setWeekPickerOpen(false);
    onNextWeek();
  };

  const handleCurrent = () => {
    setWeekPickerOpen(false);
    onCurrentWeek();
  };

  const handleWeekSelect = (ymd: string) => {
    if (ymd) onWeekSelect(ymd);
    setWeekPickerOpen(false);
  };

  return (
    <div className={`flex items-center flex-wrap gap-3 ${className}`}>
      <button
        type="button"
        onClick={handlePrevious}
        disabled={disabled}
        className={styles.navBtn}
        aria-label="Previous week"
      >
        <ChevronLeft className={variant === 'neutral' ? 'h-5 w-5 text-blue-600' : 'h-4 w-4'} />
      </button>

      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => !disabled && setWeekPickerOpen((open) => !open)}
          disabled={disabled}
          className={styles.pickerBtn}
          aria-label="Select week"
          aria-expanded={weekPickerOpen}
        >
          <Calendar
            className={`shrink-0 text-gray-500 ${variant === 'neutral' ? 'h-5 w-5' : 'h-5 w-5'}`}
            aria-hidden
          />
          <span className={styles.dateText}>{weekRangeLabel}</span>
          <ChevronRight className="h-4 w-4 text-gray-400 ml-auto shrink-0" aria-hidden />
        </button>
        {weekPickerOpen && !disabled && (
          <div className="absolute left-0 top-full mt-1 z-50 shadow-lg rounded-lg">
            <WeekPickerCalendar
              value={toLocalDateString(weekStart)}
              onChange={handleWeekSelect}
              onClose={() => setWeekPickerOpen(false)}
              weekStartsOn={weekStartsOn}
            />
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={disabled}
        className={styles.navBtn}
        aria-label="Next week"
      >
        <ChevronRight className={variant === 'neutral' ? 'h-5 w-5 text-blue-600' : 'h-4 w-4'} />
      </button>

      {showCurrentWeekButton && (
        <button
          type="button"
          onClick={handleCurrent}
          disabled={disabled || isCurrentWeek}
          className={styles.currentBtn}
        >
          {currentWeekLabel}
        </button>
      )}

      {endContent}
    </div>
  );
}
